"use server";

import { redirect } from "next/navigation";
import { db } from "@/lib/db";
import { setSession } from "@/lib/session";
import { normaliseIndianMobile } from "@/lib/phone";
import { checkOtp, deliveryHint, issueOtp } from "@/lib/otp";
import { sendOtp } from "@/lib/otp-sender";
import { rateLimit } from "@/lib/rate-limit";

export type LoginState = {
  step: "phone" | "otp";
  phone?: string;
  error?: string;
  /** what happened to the code — sent, or to be relayed by the office */
  hint?: string;
};

/** Party types that may not log in — Notion "PARTY TYPE " values, comma-separated in env. */
function blockedPartyTypes(): Set<string> {
  return new Set(
    (process.env.LOGIN_BLOCKED_PARTY_TYPES ?? "")
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
  );
}

async function loginDealer(phone: string) {
  const dealer = await db.dealer.findFirst({ where: { phone, active: true } });
  if (!dealer) return null;
  const blocked = blockedPartyTypes();
  if (dealer.partyType && blocked.has(dealer.partyType.trim().toUpperCase())) return null;
  return dealer;
}

export async function requestOtp(_prev: LoginState, form: FormData): Promise<LoginState> {
  const phone = normaliseIndianMobile(String(form.get("phone") ?? ""));
  if (!phone) return { step: "phone", error: "Enter a 10-digit Indian mobile number." };

  const limit = rateLimit(`login:${phone}`, 8, 60 * 60 * 1000);
  if (!limit.ok) {
    return { step: "phone", phone, error: "Too many attempts for this number. Try again later or call the office." };
  }

  const dealer = await loginDealer(phone);
  if (!dealer) {
    return {
      step: "phone",
      phone,
      error: "This number is not registered as a Sportsun dealer. If your number changed, tell the office; if you are new, request a login below.",
    };
  }

  const issued = await issueOtp(phone);
  if (!issued.ok) return { step: "phone", phone, error: issued.error };
  const sent = await sendOtp(phone, issued.code);
  return { step: "otp", phone, hint: deliveryHint(sent.sent, sent.channel) };
}

export async function verifyOtp(_prev: LoginState, form: FormData): Promise<LoginState> {
  const phone = normaliseIndianMobile(String(form.get("phone") ?? ""));
  const code = String(form.get("code") ?? "").trim();
  if (!phone) return { step: "phone", error: "Start again — number not recognised." };

  const dealer = await loginDealer(phone);
  if (!dealer) return { step: "phone", error: "Start again — number not recognised." };

  const result = await checkOtp(phone, code);
  if (!result.ok) return { step: "otp", phone, error: result.error };

  await setSession({ kind: "dealer", id: dealer.id });
  redirect("/");
}
